import { Box } from '@material-ui/core';
import { formatDateTimeReadable, Pricing } from '@nemuru-eng/js-api-client';
import { useNavigate } from 'react-router-dom';

import { PricingSpecsTable } from '@/components/modules/AgentDetail/ProvidersDetails/components/AgentPricings/components/PricingSpecsTable/PricingSpecsTable';
import { Font } from '@/components/shared/components/Font';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { PricingTag } from '@/components/shared/components/PricingsTable/components/PricingTag';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useUser } from '@/hooks/useUser';

interface PricingDetailDialogProps {
  pricing: Pricing;
  opened: boolean;
  setOpened: (boolean) => void;
  handleClose;
  refreshData: () => Promise<unknown>;
  editModeAllowed?: boolean;
}

export const PricingDetailDialog = ({
  pricing,
  opened,
  setOpened,
  handleClose,
  refreshData,
  editModeAllowed,
}: PricingDetailDialogProps) => {
  const navigate = useNavigate();
  const { userEntity } = useUser();

  const handleNavigateToEditor = () => {
    navigate(`/pricing/${pricing.id}`);
  };

  if (!userEntity) {
    return null;
  }

  return (
    <NemuruDialog
      avoidDefaultClose
      opened={opened}
      setOpened={setOpened}
      dialogProps={{ maxWidth: 'lg' }}
      buttons={
        <>
          {userEntity.isRoleAdminOrSuperAdmin() && (
            <StandardButton onClick={handleNavigateToEditor}>
              <Text tId="OPEN_PRICING_EDITOR" />
            </StandardButton>
          )}
          <StandardButton main onClick={handleClose}>
            <Text tId="CLOSE" />
          </StandardButton>
        </>
      }
    >
      <Box mt={2} display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <Font variant="h3" color="textPrimary">
          {pricing.name}
        </Font>
        <Box display={'flex'} gridGap={16}>
          {pricing.tags.map((tag) => (
            <PricingTag key={`${pricing.id}-${tag}`} tag={tag} />
          ))}
        </Box>
      </Box>
      <Box mb={3}>
        <Font variant={'caption'} color={'textSecondary'}>
          <Text tId="CREATED_AT" /> {formatDateTimeReadable(pricing.createdAt)}
          {' | '}
          <Text tId="UPDATED_AT" /> {formatDateTimeReadable(pricing.updatedAt)}
        </Font>
      </Box>
      {userEntity.isRoleAdminOrSuperAdmin() && (
        <Box mb={3}>
          <Font variant={'body2'} color={'textSecondary'}>
            {pricing.description}
          </Font>
        </Box>
      )}

      <PricingSpecsTable pricing={pricing} refreshData={refreshData} editModeAllowed={editModeAllowed} />
    </NemuruDialog>
  );
};
